/**
 * LicenciaSection — Tab "Licencia" en Ajustes.
 *
 * Permite al admin:
 *  - Ver el estado actual de la licencia (plan, clientes, modo, expiración)
 *  - Cambiar la license_key (POST /api/v1/licensing/activate)
 *  - Forzar un heartbeat inmediato (POST /api/v1/licensing/heartbeat)
 *
 * Solo visible para usuarios con permiso ajustes.write.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldX, ShieldAlert, ShieldOff,
  RefreshCw, Loader2, Key, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiCall, type ApiError } from "@/lib/api";
import { getTz } from "@/lib/tz";

/* ─── Tipos ────────────────────────────────────────────────── */
type LicenseStatus = {
  enabled: boolean;
  mode: "off" | "active" | "grace" | "blocked" | "no_license";
  message: string;
  grace_days_remaining: number | null;
  plan: string | null;
  max_clients: number | null;
  current: {
    id: string;
    license_key: string;
    status: string;
    expires_at: string | null;
    last_validated_at: string | null;
    plan: string | null;
    max_clients: number | null;
  } | null;
};

type ActivateResponse = {
  license: { status: string; plan: string | null; max_clients: number | null };
  validated_remote: boolean;
  message: string;
};

type HeartbeatResponse = {
  ok: boolean;
  status: string;
  message: string;
  last_validated_at: string | null;
  next_heartbeat_at: string | null;
};

/* ─── Helpers ───────────────────────────────────────────────── */
const PLAN_NAMES: Record<string, string> = {
  starter:    "Starter (50 clientes)",
  basic:      "Basic (100 clientes)",
  standard:   "Standard (200 clientes)",
  pro:        "Pro (300 clientes)",
  business:   "Business (500 clientes)",
  enterprise: "Enterprise (1,000 clientes)",
  unlimited:  "Unlimited (sin límite)",
};

function planLabel(plan: string | null): string {
  if (!plan) return "Sin plan asignado";
  return PLAN_NAMES[plan.toLowerCase()] ?? plan;
}

function modeIcon(mode: string) {
  switch (mode) {
    case "active":    return <ShieldCheck size={18} className="text-green-500" />;
    case "grace":     return <ShieldAlert size={18} className="text-yellow-500" />;
    case "blocked":   return <ShieldX size={18} className="text-red-500" />;
    default:          return <ShieldOff size={18} className="text-muted-foreground" />;
  }
}

function modeBadge(mode: string) {
  const map: Record<string, string> = {
    active:     "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    grace:      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    blocked:    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    off:        "bg-muted text-muted-foreground",
    no_license: "bg-muted text-muted-foreground",
  };
  const label: Record<string, string> = {
    active: "Activa", grace: "Gracia", blocked: "Bloqueada",
    off: "Apagada", no_license: "Sin licencia",
  };
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${map[mode] ?? map.off}`}>
      {label[mode] ?? mode}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-MX", {
    timeZone: getTz(),
    dateStyle: "medium", timeStyle: "short",
  });
}

/* ─── Hooks ─────────────────────────────────────────────────── */
function useLicenseStatus() {
  return useQuery<LicenseStatus>({
    queryKey: ["licensing-status"],
    queryFn: () => apiCall<LicenseStatus>("get", "/api/v1/licensing/status"),
    staleTime: 30_000,
  });
}

/* ─── Componente principal ──────────────────────────────────── */
export function LicenciaSection() {
  const qc = useQueryClient();
  const { data: status, isLoading, isError } = useLicenseStatus();
  const [licenseKey, setLicenseKey] = useState("");

  /* Activar / cambiar licencia */
  const activateMut = useMutation<ActivateResponse, ApiError, string>({
    mutationFn: (key) =>
      apiCall<ActivateResponse>("post", "/api/v1/licensing/activate", { json: { license_key: key } }),
    onSuccess: (res) => {
      toast.success(
        res.validated_remote
          ? "Licencia validada correctamente con ByteControl"
          : "Licencia guardada (modo gracia — sin conexión a ByteControl)",
      );
      setLicenseKey("");
      qc.invalidateQueries({ queryKey: ["licensing-status"] });
    },
    onError: (err) => {
      toast.error(err.message ?? "Error al activar la licencia");
    },
  });

  /* Heartbeat manual */
  const heartbeatMut = useMutation<HeartbeatResponse, ApiError>({
    mutationFn: () => apiCall<HeartbeatResponse>("post", "/api/v1/licensing/heartbeat"),
    onSuccess: (res) => {
      toast[res.ok ? "success" : "warning"](res.message);
      qc.invalidateQueries({ queryKey: ["licensing-status"] });
    },
    onError: (err) => {
      toast.error(err.message ?? "Error en el heartbeat");
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-muted-foreground text-sm">
        <Loader2 size={16} className="animate-spin" />
        Cargando estado de licencia…
      </div>
    );
  }

  if (isError || !status) {
    return (
      <div className="flex items-center gap-2 py-8 text-destructive text-sm">
        <AlertCircle size={16} />
        No se pudo obtener el estado de licencia.
      </div>
    );
  }

  const cur = status.current;

  return (
    <div className="flex flex-col gap-6 max-w-xl">
      {/* ── Estado actual ── */}
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          {modeIcon(status.mode)}
          <span className="font-medium text-sm">Estado de licencia</span>
          {modeBadge(status.mode)}
        </div>
        <p className="text-xs text-muted-foreground">{status.message}</p>

        {cur && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
            <span className="font-medium text-foreground">Clave</span>
            <span className="font-mono">{cur.license_key}</span>
            <span className="font-medium text-foreground">Plan</span>
            <span>{planLabel(status.plan ?? cur.plan)}</span>
            {(status.max_clients ?? cur.max_clients) !== null && (
              <>
                <span className="font-medium text-foreground">Límite clientes</span>
                <span>{status.max_clients ?? cur.max_clients} clientes</span>
              </>
            )}
            <span className="font-medium text-foreground">Vence</span>
            <span>{fmtDate(cur.expires_at)}</span>
            <span className="font-medium text-foreground">Última validación</span>
            <span>{fmtDate(cur.last_validated_at)}</span>
          </div>
        )}

        {status.mode === "grace" && status.grace_days_remaining !== null && (
          <div className="rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-3 py-2 text-xs text-yellow-800 dark:text-yellow-300">
            ⚠ Período de gracia — quedan <strong>{status.grace_days_remaining} días</strong> sin heartbeat exitoso. Verifica la conexión con ByteControl.
          </div>
        )}

        {status.mode === "blocked" && (
          <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-800 dark:text-red-300">
            🔒 Licencia bloqueada. El acceso al sistema está restringido. Contacta a tu proveedor o actualiza la clave de licencia.
          </div>
        )}

        {/* Botón verificar ahora */}
        <div className="flex justify-end pt-1">
          <Button
            variant="outline"
            size="sm"
            disabled={heartbeatMut.isPending}
            onClick={() => heartbeatMut.mutate()}
          >
            {heartbeatMut.isPending
              ? <Loader2 size={14} className="animate-spin mr-1" />
              : <RefreshCw size={14} className="mr-1" />
            }
            Verificar ahora
          </Button>
        </div>
      </div>

      {/* ── Cambiar / registrar licencia ── */}
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Key size={15} className="text-muted-foreground" />
          <span className="font-medium text-sm">
            {cur ? "Cambiar licencia" : "Registrar licencia"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Introduce la clave de licencia proporcionada por tu proveedor de ByteISP.
          Al confirmar se validará en tiempo real con ByteControl.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="license-key" className="text-xs">Clave de licencia</Label>
          <div className="flex gap-2">
            <Input
              id="license-key"
              placeholder="BC-XXXX-XXXX-XXXX-XXXX"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              className="font-mono text-sm flex-1"
              disabled={activateMut.isPending}
            />
            <Button
              size="sm"
              disabled={!licenseKey.trim() || activateMut.isPending}
              onClick={() => activateMut.mutate(licenseKey.trim())}
            >
              {activateMut.isPending
                ? <Loader2 size={14} className="animate-spin mr-1" />
                : <CheckCircle2 size={14} className="mr-1" />
              }
              Activar
            </Button>
          </div>
        </div>
      </div>

      {/* ── Info de planes ── */}
      <div className="rounded-lg border bg-card p-4 flex flex-col gap-2">
        <p className="text-xs font-medium">Planes disponibles</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
          {Object.entries(PLAN_NAMES).map(([key, label]) => (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  (status.plan ?? cur?.plan) === key
                    ? "bg-primary"
                    : "bg-muted-foreground/40"
                }`}
              />
              {label}
              {(status.plan ?? cur?.plan) === key && (
                <span className="text-primary font-medium ml-1">← actual</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
